'use client'

import { useMemo, useState, type CSSProperties } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { BackgroundLayers } from '@/components/ui'
import FadeUp from '@/components/ui/FadeUp'
import { buildContactPayload } from '@/lib/contact/build-contact-payload'
import {
  buildContactDefaultValues,
  buildContactFormSchema,
  fieldKey,
  hasCheckboxOptions,
  sanitizeNumberInput,
  sanitizePhoneInput,
  type ContactFormField,
} from '@/lib/contact/contact-form-utils'
import {
  getRecaptchaSiteKey,
  getRecaptchaToken,
  loadRecaptchaScript,
} from '@/lib/contact/recaptcha'
import { submitContactForm } from '@/lib/contact/submit-contact-form'
import {
  ctaThemeFromLayout,
  resolveButtonClass,
  resolveContactSubmitButtonClass,
} from '@/lib/cta'
import { getStrapiMedia } from '@/lib/strapi/client'
import type { ButtonType } from '@/lib/types/shared'
import type { FormFieldType } from '@/lib/types/contact'
import type { TeaserContactFormType } from '@/lib/types/teaser'
import {
  contentAlignClass,
  isLayoutB,
} from '@/lib/utils/section-styles'

type FormValues = Record<string, string | boolean>

type ContactPerson = NonNullable<NonNullable<TeaserContactFormType['contacts']>[number]>

type Props = {
  preHeading?: string | null
  heading: string | null
  formHeading?: string | null
  description: string | null
  contacts?: TeaserContactFormType['contacts']
  phoneIcon?: TeaserContactFormType['phoneIcon']
  formFields: TeaserContactFormType['formFields']
  submitButtonLabel: string | null
  submitButtonStyle: ButtonType | null
  disclaimer: string | null
  layout?: string | null
  configuration: NonNullable<TeaserContactFormType>['configuration']
}

const fieldBgClass = 'bg-[var(--contact-field-bg,#ffffff)]'

/** Layout B cream card — Figma: white fill, #CED8C6 border, 4px→2px radius. */
const INPUT_BASE_A = `w-full h-[42px] md:h-[53px] ${fieldBgClass} border border-border rounded md:rounded-[2px] px-4 py-3 md:py-[15px] font-body text-base font-normal leading-5 md:text-sm text-forest placeholder:text-stone focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary transition-colors aria-[invalid=true]:border-[var(--color-error)]`

/** Layout A split form — pill inputs on white card. */
const INPUT_BASE_B = `w-full h-[45px] md:h-[51px] ${fieldBgClass} border-0 rounded-[24px] md:rounded-[40px] px-4 py-3 md:px-5 md:py-[15px] font-body text-base font-normal leading-5 md:text-sm text-forest placeholder:text-muted md:placeholder:text-stone focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-[var(--color-error)]`

function FieldLabel({
  htmlFor,
  label,
  required,
  layoutB = false,
}: {
  htmlFor: string
  label: string
  required: boolean
  layoutB?: boolean
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={
        layoutB
          ? 'font-body text-[13px] font-semibold leading-[17px] tracking-normal text-form-label md:text-sm md:font-normal md:leading-5'
          : 'font-body text-[13px] font-medium leading-[17px] tracking-normal text-form-label md:text-sm md:font-normal md:leading-5'
      }
    >
      {label}
      {required && (
        <span className="ml-0.5 text-[#EF4444]" aria-hidden="true">
          {' '}
          *
        </span>
      )}
      {required && <span className="sr-only"> (required)</span>}
    </label>
  )
}

function FieldError({ id, message }: { id: string; message: string | undefined }) {
  if (!message) return null
  return (
    <p id={id} className="mt-1 text-nav font-body text-[var(--color-error)]" role="alert">
      {message}
    </p>
  )
}

function isCompactField(field: ContactFormField) {
  return (
    field.fieldType !== 'Long Text' &&
    field.fieldType !== 'Boolean' &&
    field.fieldType !== 'Check Box' &&
    field.fieldType !== 'Radio Button'
  )
}

/** US display format for date fields (all browsers). */
const DATE_DISPLAY_PLACEHOLDER = 'mm/dd/yyyy'

/** Native date inputs store ISO (yyyy-mm-dd); UI always shows mm/dd/yyyy. */
function formatIsoDateDisplay(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!match) return DATE_DISPLAY_PLACEHOLDER
  return `${match[2]}/${match[3]}/${match[1]}`
}

/** Open the native date picker (Chrome/Safari/Firefox/Edge). */
function openNativeDatePicker(input: HTMLInputElement) {
  try {
    if (typeof input.showPicker === 'function') {
      input.showPicker()
      return
    }
  } catch {
    /* NotAllowedError / unsupported — fall through to focus */
  }
  input.focus()
}

type FieldOption = NonNullable<FormFieldType['options']>[number] & { point: string }

/** Studio first, then numeric bedroom counts ascending (1, 2, 3…), then other labels. */
function sortBedroomCountOptions(
  options: FieldOption[],
  fieldName: string,
): FieldOption[] {
  if (!/bedroom/i.test(fieldName)) return options

  const rank = (point: string) => {
    const t = point.trim().toLowerCase()
    if (t === 'studio' || t.startsWith('studio')) return { group: 0, n: 0 }
    const n = Number.parseInt(t.replace(/[^\d]/g, ''), 10)
    if (!Number.isNaN(n)) return { group: 1, n }
    return { group: 2, n: 0 }
  }

  return [...options].sort((a, b) => {
    const ra = rank(a.point)
    const rb = rank(b.point)
    if (ra.group !== rb.group) return ra.group - rb.group
    if (ra.group === 1) return ra.n - rb.n
    return a.point.localeCompare(b.point)
  })
}

function getDropdownOptions(field: ContactFormField): {
  options: FieldOption[]
  emptyLabel: string
} {
  const placeholder = field.placeHolder?.trim() || undefined
  const raw = (field.options ?? []).filter(
    (o): o is FieldOption => o.point !== null && Boolean(o.point.trim()),
  )
  const options = sortBedroomCountOptions(raw, field.fieldName)

  // Only use CMS placeholder as the empty label when it is not also a real option.
  // Otherwise the select looks "filled" with that option while value stays "".
  const placeholderIsRealOption =
    !!placeholder &&
    options.some((o) => o.point.trim().toLowerCase() === placeholder.toLowerCase())

  return {
    options,
    emptyLabel: placeholderIsRealOption ? 'Select' : (placeholder ?? 'Select'),
  }
}

function groupFields(fields: ContactFormField[]): ContactFormField[][] {
  const groups: ContactFormField[][] = []
  let i = 0
  while (i < fields.length) {
    const current = fields[i]
    const next = fields[i + 1]
    if (next && isCompactField(current) && isCompactField(next)) {
      groups.push([current, next])
      i += 2
    } else {
      groups.push([current])
      i += 1
    }
  }
  return groups
}

function PhoneIconFallback() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.85 21 3 13.15 3 3a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.25 1.02l-2.2 2.19Z"
        fill="currentColor"
      />
    </svg>
  )
}

function ContactPersonRow({
  person,
  phoneIconUrl,
}: {
  person: ContactPerson
  phoneIconUrl: string | null
}) {
  const phone = person.phoneNumber?.trim()
  const email = person.email?.trim()

  if (!person.name?.trim() && !phone && !email) return null

  return (
    <div className="flex w-full items-center gap-4">
      <span
        className="flex size-10 shrink-0 items-center justify-center rounded-[12px] border border-border bg-surface-white text-icon-fill md:size-[52px] md:rounded-[20px] md:border-0 md:shadow-[0px_1px_2px_rgba(0,0,0,0.05)]"
        aria-hidden="true"
      >
        {phoneIconUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={phoneIconUrl} alt="" className="size-5 object-contain md:size-6" />
        ) : (
          <span className="flex size-5 items-center justify-center md:size-6 [&_svg]:h-full [&_svg]:w-full">
            <PhoneIconFallback />
          </span>
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 md:gap-1">
        {person.name?.trim() && (
          <p className="font-heading text-base font-bold leading-[18px] text-contact-name md:text-lg md:leading-[26px]">
            {person.name}
          </p>
        )}
        {(phone || email) && (
          <p className="flex min-w-0 flex-wrap items-baseline gap-x-1 font-body text-sm font-normal leading-[150%] text-text-primary md:text-base md:leading-6">
            {phone && (
              <a
                href={`tel:${phone.replace(/\s+/g, '')}`}
                className="shrink-0 whitespace-nowrap hover:underline focus-visible:outline-none focus-visible:underline"
              >
                {phone}
              </a>
            )}
            {phone && email ? (
              <span className="shrink-0" aria-hidden="true">
                |
              </span>
            ) : null}
            {email && (
              <a
                href={`mailto:${email}`}
                className="max-w-full whitespace-nowrap hover:underline focus-visible:outline-none focus-visible:underline"
              >
                {email}
              </a>
            )}
          </p>
        )}
      </div>
    </div>
  )
}

function ContactAgentCard({ person }: { person: ContactPerson }) {
  const name = person.name?.trim()
  const phone = person.phoneNumber?.trim()
  const email = person.email?.trim()

  if (!name && !phone && !email) return null

  return (
    <div className="box-border flex h-full w-full flex-col items-start gap-2 rounded border border-[rgba(151,152,155,0.2)] bg-surface-white p-4 md:gap-2 md:rounded-[2px] md:px-4 md:py-3">
      <div className="flex w-full flex-col items-start gap-2 md:gap-2.5">
        {name && (
          <p className="w-full font-body text-base font-bold leading-[21px] text-contact-name md:text-lg md:leading-[23px]">
            {name}
          </p>
        )}
        {(phone || email) && (
          <div className="flex w-full flex-col items-start font-body text-[13px] font-normal leading-[18px] text-text-primary md:text-sm md:leading-5">
            {phone && (
              <a href={`tel:${phone.replace(/\s+/g, '')}`} className="hover:underline">
                {phone}
              </a>
            )}
            {email && (
              <a href={`mailto:${email}`} className="break-all hover:underline">
                {email}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ContactAgentsSection({
  people,
  title,
  className,
}: {
  people: ContactPerson[]
  title: string
  className?: string
}) {
  if (people.length === 0) return null

  return (
    <div
      data-fig="teaser-contact-agents"
      className={['flex w-full flex-col items-start gap-5 md:items-center md:gap-6', className]
        .filter(Boolean)
        .join(' ')}
    >
      <h3 className="w-full font-heading text-[28px] font-bold leading-8 text-[#1C1C1A] md:w-auto md:text-[32px] md:leading-9 md:text-ink">
        {title}
      </h3>
      <ul
        className="flex w-full flex-col items-start gap-2 self-stretch md:flex-row md:items-stretch md:gap-3"
        role="list"
      >
        {people.map((person) => (
          <li key={person.id} className="w-full self-stretch md:min-w-0 md:flex-1" role="listitem">
            <ContactAgentCard person={person} />
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function TeaserContactFormClient({
  preHeading = null,
  heading,
  formHeading = null,
  description,
  contacts = null,
  phoneIcon = null,
  formFields,
  submitButtonLabel,
  submitButtonStyle,
  disclaimer,
  layout,
  configuration,
}: Props) {
  // Layout A → split (info + form); Layout B → single cream form card + agents
  const splitLayout = !isLayoutB(layout)
  const inputBase = splitLayout ? INPUT_BASE_B : INPUT_BASE_A
  const fieldStackClass = splitLayout
    ? 'flex flex-col gap-1.5'
    : 'flex flex-col gap-1.5 md:gap-2'
  const radioDotClass = splitLayout ? 'bg-accent' : 'bg-brand-primary'
  const requiredMarkClass = 'ml-0.5 text-[#EF4444]'

  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle')
  const [serverError, setServerError] = useState<string | null>(null)
  const recaptchaSiteKey = getRecaptchaSiteKey()

  const validFields = useMemo<ContactFormField[]>(
    () =>
      (formFields ?? []).filter(
        (f): f is ContactFormField => f.fieldName !== null && f.fieldType !== null,
      ),
    [formFields],
  )

  const schema = useMemo(() => buildContactFormSchema(validFields), [validFields])
  const defaultValues = useMemo(
    () => buildContactDefaultValues(validFields),
    [validFields],
  )

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema) as any,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  })

  const prefetchRecaptcha = () => {
    if (!recaptchaSiteKey) return
    loadRecaptchaScript(recaptchaSiteKey).catch(() => {
      // Prefetch failure is non-fatal; submit will surface an error if needed.
    })
  }

  async function onSubmit(data: FormValues) {
    setSubmitStatus('submitting')
    setServerError(null)
    try {
      if (!recaptchaSiteKey) {
        throw new Error('reCAPTCHA is not configured. Please try again later.')
      }

      const recaptchaToken = await getRecaptchaToken(
        recaptchaSiteKey,
        'teaser_contact_form',
      )
      const payload = buildContactPayload(validFields, data, recaptchaToken)
      await submitContactForm(payload)
      setSubmitStatus('success')
      reset()
    } catch (err) {
      setSubmitStatus('error')
      setServerError(
        err instanceof Error ? err.message : 'Submission failed. Please try again.',
      )
    }
  }

  function renderField(field: ContactFormField) {
    const name = fieldKey(field)
    const fieldId = `teaser-field-${field.id}`
    const errorId = `${fieldId}-err`
    const hasError = !!errors[name]
    const errorMsg = errors[name]?.message as string | undefined
    const describedBy = hasError ? errorId : undefined
    const isRequired = field.isRequired === true
    const placeholder = field.placeHolder?.trim() || undefined

    if (field.fieldType === 'Dropdown') {
      const { options, emptyLabel } = getDropdownOptions(field)
      return (
        <div key={field.id} className={fieldStackClass}>
          <FieldLabel
            htmlFor={fieldId}
            label={field.fieldName}
            required={isRequired}
            layoutB={splitLayout}
          />
          <div className="relative">
            <select
              id={fieldId}
              {...register(name)}
              aria-required={isRequired || undefined}
              aria-invalid={hasError || undefined}
              aria-describedby={describedBy}
              className={`${inputBase} appearance-none pr-10`}
            >
              <option value="">{emptyLabel}</option>
              {options.map((opt) => (
                <option key={opt.id} value={opt.point}>
                  {opt.point}
                </option>
              ))}
            </select>
            <span
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2"
              aria-hidden="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4 text-text-dark"
              >
                <path d="m4 6 4 4 4-4" />
              </svg>
            </span>
          </div>
          <FieldError id={errorId} message={errorMsg} />
        </div>
      )
    }

    if (field.fieldType === 'Radio Button') {
      const options = sortBedroomCountOptions(
        (field.options ?? []).filter(
          (o): o is FieldOption => o.point !== null && Boolean(o.point.trim()),
        ),
        field.fieldName,
      )
      return (
        <fieldset
          key={field.id}
          className={fieldStackClass}
          role="radiogroup"
          aria-required={isRequired || undefined}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
        >
          <legend
            className={
              splitLayout
                ? 'pb-0 font-body text-[13px] font-semibold leading-[17px] text-form-label md:text-sm md:font-normal md:leading-5'
                : 'pb-0 font-body text-[13px] font-medium leading-[17px] text-form-label md:text-sm md:font-normal md:leading-5'
            }
          >
            {field.fieldName}
            {isRequired && (
              <span className={requiredMarkClass} aria-hidden="true">
                {' '}
                *
              </span>
            )}
            {isRequired && <span className="sr-only"> (required)</span>}
          </legend>
          <Controller
            name={name}
            control={control}
            render={({ field: rhfField }) => (
              <div
                className={
                  splitLayout
                    ? 'flex flex-wrap content-start gap-5 py-2'
                    : 'flex flex-wrap content-start gap-5 py-2 md:gap-4'
                }
              >
                {options.map((opt) => {
                  const checked = rhfField.value === opt.point
                  return (
                    <label
                      key={opt.id}
                      className={
                        splitLayout
                          ? 'flex cursor-pointer items-center gap-2 font-body text-sm font-normal leading-5 text-forest'
                          : 'flex cursor-pointer items-center gap-2 font-body text-sm font-normal leading-[18px] text-stone md:leading-5 md:text-forest'
                      }
                    >
                      <input
                        type="radio"
                        value={opt.point}
                        checked={checked}
                        onChange={() => rhfField.onChange(opt.point)}
                        onBlur={rhfField.onBlur}
                        required={isRequired}
                        className="sr-only"
                      />
                      <span
                        className={`flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                          checked ? 'border-text-dark' : `border-border ${fieldBgClass}`
                        }`}
                        aria-hidden="true"
                      >
                        <span
                          className={`size-2.5 rounded-full ${radioDotClass} transition-opacity ${
                            checked ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                      </span>
                      {opt.point}
                    </label>
                  )
                })}
              </div>
            )}
          />
          <FieldError id={errorId} message={errorMsg} />
        </fieldset>
      )
    }

    if (field.fieldType === 'Boolean') {
      const yesNoOptions = [
        { id: 'yes', label: 'Yes', value: true },
        { id: 'no', label: 'No', value: false },
      ] as const
      return (
        <fieldset
          key={field.id}
          className={fieldStackClass}
          role="radiogroup"
          aria-required={isRequired || undefined}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
        >
          <legend className="pb-2 text-label font-body font-normal tracking-normal text-form-label">
            {field.fieldName}
            {isRequired && (
              <span className={requiredMarkClass} aria-hidden="true">
                {' '}
                *
              </span>
            )}
            {isRequired && <span className="sr-only"> (required)</span>}
          </legend>
          <Controller
            name={name}
            control={control}
            render={({ field: rhfField }) => (
              <div className="flex flex-wrap gap-spacing-gap-md">
                {yesNoOptions.map((opt) => {
                  const checked = rhfField.value === opt.value
                  return (
                    <label
                      key={opt.id}
                      className="flex cursor-pointer items-center gap-2 text-nav font-body text-text-primary"
                    >
                      <input
                        type="radio"
                        value={String(opt.value)}
                        checked={checked}
                        onChange={() => rhfField.onChange(opt.value)}
                        onBlur={rhfField.onBlur}
                        required={isRequired}
                        className="sr-only"
                      />
                      <span
                        className={`flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                          checked ? 'border-text-dark' : `border-border ${fieldBgClass}`
                        }`}
                        aria-hidden="true"
                      >
                        <span
                          className={`size-2.5 rounded-full ${radioDotClass} transition-opacity ${
                            checked ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                      </span>
                      {opt.label}
                    </label>
                  )
                })}
              </div>
            )}
          />
          <FieldError id={errorId} message={errorMsg} />
        </fieldset>
      )
    }

    if (field.fieldType === 'Check Box') {
      const options = (field.options ?? []).filter(
        (
          o,
        ): o is NonNullable<FormFieldType['options']>[number] & { point: string } =>
          o.point !== null,
      )

      if (hasCheckboxOptions(field)) {
        return (
          <Controller
            key={field.id}
            name={name}
            control={control}
            render={({ field: rhfField }) => {
              const currentValue =
                typeof rhfField.value === 'string' ? rhfField.value : ''
              const selected = currentValue
                ? currentValue.split(',').filter(Boolean)
                : []

              const toggle = (text: string) => {
                const next = selected.includes(text)
                  ? selected.filter((t) => t !== text)
                  : [...selected, text]
                rhfField.onChange(next.length > 0 ? next.join(',') : '')
              }

              return (
                <fieldset
                  className={fieldStackClass}
                  aria-describedby={describedBy}
                >
                  <legend className="pb-2 text-label font-body font-normal tracking-normal text-form-label">
                    {field.fieldName}
                    {isRequired && (
                      <span
                        className={requiredMarkClass}
                        aria-hidden="true"
                      >
                        {' '}
                        *
                      </span>
                    )}
                    {isRequired && <span className="sr-only"> (required)</span>}
                  </legend>
                  <div className="flex flex-wrap gap-spacing-gap-md">
                    {options.map((opt) => {
                      const checked = selected.includes(opt.point)
                      return (
                        <label
                          key={opt.id}
                          className="flex cursor-pointer items-center gap-2 text-nav font-body text-text-primary"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(opt.point)}
                            onBlur={rhfField.onBlur}
                            aria-required={isRequired || undefined}
                            className="sr-only"
                          />
                          <span
                            className={`flex size-5 shrink-0 items-center justify-center rounded-xs border transition-colors ${
                              checked
                                ? 'border-brand-primary bg-brand-primary'
                                : `${fieldBgClass} border-border`
                            }`}
                            aria-hidden="true"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 12 12"
                              fill="none"
                              stroke="white"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className={`h-3 w-3 transition-opacity ${checked ? 'opacity-100' : 'opacity-0'}`}
                              aria-hidden="true"
                            >
                              <path d="m2 6 3 3 5-5" />
                            </svg>
                          </span>
                          {opt.point}
                        </label>
                      )
                    })}
                  </div>
                  <FieldError id={errorId} message={errorMsg} />
                </fieldset>
              )
            }}
          />
        )
      }

      return (
        <Controller
          key={field.id}
          name={name}
          control={control}
          render={({ field: rhfField }) => {
            const checked = rhfField.value as boolean
            return (
              <div className="flex items-start gap-3 pt-2">
                <label htmlFor={fieldId} className="mt-0.5 shrink-0 cursor-pointer">
                  <input
                    id={fieldId}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => rhfField.onChange(e.target.checked)}
                    onBlur={rhfField.onBlur}
                    aria-required={isRequired || undefined}
                    aria-invalid={hasError || undefined}
                    aria-describedby={describedBy}
                    className="sr-only"
                  />
                  <span
                    className={`flex size-5 items-center justify-center rounded-xs border transition-colors focus-within:ring-2 focus-within:ring-brand-primary ${
                      checked
                        ? 'border-brand-primary bg-brand-primary'
                        : `${fieldBgClass} border-border`
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="white"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`h-3 w-3 transition-opacity ${checked ? 'opacity-100' : 'opacity-0'}`}
                      aria-hidden="true"
                    >
                      <path d="m2 6 3 3 5-5" />
                    </svg>
                  </span>
                </label>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <label
                    htmlFor={fieldId}
                    className="cursor-pointer text-nav font-body leading-snug text-text-primary"
                  >
                    {field.fieldName}
                    {isRequired && (
                      <span
                        className={requiredMarkClass}
                        aria-hidden="true"
                      >
                        {' '}
                        *
                      </span>
                    )}
                    {isRequired && <span className="sr-only"> (required)</span>}
                  </label>
                  <FieldError id={errorId} message={errorMsg} />
                </div>
              </div>
            )
          }}
        />
      )
    }

    if (field.fieldType === 'Long Text') {
      return (
        <div key={field.id} className={fieldStackClass}>
          <FieldLabel
            htmlFor={fieldId}
            label={field.fieldName}
            required={isRequired}
            layoutB={splitLayout}
          />
          <textarea
            id={fieldId}
            rows={4}
            placeholder={placeholder}
            {...register(name)}
            aria-required={isRequired || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={describedBy}
            className={
              splitLayout
                ? `h-[148px] md:h-[116px] w-full resize-none rounded-[12px] border-0 px-4 py-3 md:px-5 font-body text-base font-normal leading-5 md:text-sm text-forest placeholder:text-muted md:placeholder:text-stone focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-[var(--color-error)] ${fieldBgClass}`
                : `h-[124px] md:h-[118px] w-full resize-none rounded border border-border px-4 py-3 font-body text-base font-normal leading-5 md:text-sm text-forest placeholder:text-stone focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary transition-colors aria-[invalid=true]:border-[var(--color-error)] md:rounded-[2px] ${fieldBgClass}`
            }
          />
          <FieldError id={errorId} message={errorMsg} />
        </div>
      )
    }

    if (field.fieldType === 'Date') {
      // type=date value stays ISO; visible text is always mm/dd/yyyy (every browser).
      const rawDate = watch(name)
      const isoDate =
        typeof rawDate === 'string' ? rawDate.trim() : ''
      const hasDateValue = /^\d{4}-\d{2}-\d{2}$/.test(isoDate)
      const displayDate = hasDateValue
        ? formatIsoDateDisplay(isoDate)
        : DATE_DISPLAY_PLACEHOLDER
      const displayClass = hasDateValue
        ? 'text-forest'
        : splitLayout
          ? 'text-muted md:text-stone'
          : 'text-stone'
      const dateReg = register(name)

      return (
        <div key={field.id} className={fieldStackClass}>
          <FieldLabel
            htmlFor={fieldId}
            label={field.fieldName}
            required={isRequired}
            layoutB={splitLayout}
          />
          <div className="relative">
            <div
              className={`${inputBase} pointer-events-none flex items-center pr-10 ${displayClass}`}
              aria-hidden="true"
            >
              {displayDate}
            </div>
            <span
              className="pointer-events-none absolute right-4 top-1/2 z-[1] -translate-y-1/2"
              aria-hidden="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="none"
                className="h-4 w-4 text-text-dark"
              >
                <rect
                  x="2"
                  y="3"
                  width="12"
                  height="11"
                  rx="1.5"
                  stroke="currentColor"
                  strokeWidth="1.25"
                />
                <path
                  d="M5 1.5v3M11 1.5v3M2 6.5h12"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                />
                <circle cx="5.5" cy="9.5" r="0.75" fill="currentColor" />
                <circle cx="8" cy="9.5" r="0.75" fill="currentColor" />
                <circle cx="10.5" cy="9.5" r="0.75" fill="currentColor" />
                <circle cx="5.5" cy="12" r="0.75" fill="currentColor" />
                <circle cx="8" cy="12" r="0.75" fill="currentColor" />
                <circle cx="10.5" cy="12" r="0.75" fill="currentColor" />
              </svg>
            </span>
            {/*
              Input sits above the facade so clicks hit it directly (Safari fails
              click-through to opacity-0 controls). Full-size calendar indicator
              + showPicker covers Chrome, Safari, Firefox, and Edge.
            */}
            <input
              id={fieldId}
              type="date"
              lang="en-US"
              {...dateReg}
              onMouseDown={(event) => {
                // Open on mousedown so the picker appears before click/focus races.
                openNativeDatePicker(event.currentTarget)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  openNativeDatePicker(event.currentTarget)
                }
              }}
              aria-label={`${field.fieldName}, ${DATE_DISPLAY_PLACEHOLDER}`}
              aria-required={isRequired || undefined}
              aria-invalid={hasError || undefined}
              aria-describedby={describedBy}
              className="absolute inset-0 z-[2] h-full w-full cursor-pointer opacity-[0.01] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
            />
          </div>
          <FieldError id={errorId} message={errorMsg} />
        </div>
      )
    }

    if (field.fieldType === 'Email') {
      return (
        <div key={field.id} className={fieldStackClass}>
          <FieldLabel
            htmlFor={fieldId}
            label={field.fieldName}
            required={isRequired}
            layoutB={splitLayout}
          />
          <input
            id={fieldId}
            type="email"
            autoComplete="email"
            placeholder={placeholder}
            {...register(name)}
            aria-required={isRequired || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={describedBy}
            className={inputBase}
          />
          <FieldError id={errorId} message={errorMsg} />
        </div>
      )
    }

    if (field.fieldType === 'Phone Number') {
      const { onChange, ...registerRest } = register(name)
      return (
        <div key={field.id} className={fieldStackClass}>
          <FieldLabel
            htmlFor={fieldId}
            label={field.fieldName}
            required={isRequired}
            layoutB={splitLayout}
          />
          <input
            id={fieldId}
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            placeholder={placeholder}
            {...registerRest}
            onChange={(e) => {
              e.target.value = sanitizePhoneInput(e.target.value)
              onChange(e)
            }}
            aria-required={isRequired || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={describedBy}
            className={inputBase}
          />
          <FieldError id={errorId} message={errorMsg} />
        </div>
      )
    }

    if (field.fieldType === 'Number') {
      const { onChange, ...registerRest } = register(name)
      return (
        <div key={field.id} className={fieldStackClass}>
          <FieldLabel
            htmlFor={fieldId}
            label={field.fieldName}
            required={isRequired}
            layoutB={splitLayout}
          />
          <input
            id={fieldId}
            type="text"
            inputMode="decimal"
            placeholder={placeholder}
            {...registerRest}
            onChange={(e) => {
              e.target.value = sanitizeNumberInput(e.target.value)
              onChange(e)
            }}
            aria-required={isRequired || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={describedBy}
            className={inputBase}
          />
          <FieldError id={errorId} message={errorMsg} />
        </div>
      )
    }

    return (
      <div key={field.id} className={fieldStackClass}>
        <FieldLabel
          htmlFor={fieldId}
          label={field.fieldName}
          required={isRequired}
          layoutB={splitLayout}
        />
        <input
          id={fieldId}
          type="text"
          placeholder={placeholder}
          {...register(name)}
          aria-required={isRequired || undefined}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          className={inputBase}
        />
        <FieldError id={errorId} message={errorMsg} />
      </div>
    )
  }

  const sectionBg = configuration?.sectionBackgroundColor?.trim() || undefined
  const formBg = configuration?.formBackgroundColor?.trim() || undefined
  const fieldBg = configuration?.fieldBackgroundColor?.trim() || undefined
  const backgroundPattern =
    configuration?.addBackgroundPattern && configuration.backgroundPattern?.url
      ? getStrapiMedia(configuration.backgroundPattern.url)
      : null
  const headerAlignClass = contentAlignClass(
    configuration?.contentAlignment ?? 'Center',
  )
  const cardHeading = formHeading ?? (splitLayout ? null : heading)
  const sectionHeading = splitLayout ? heading : (formHeading ?? heading)
  const agentsHeading =
    formHeading?.trim() &&
    heading?.trim() &&
    formHeading.trim() !== heading.trim()
      ? heading.trim()
      : 'Contact our agents'
  const people = (contacts ?? []).filter(
    (c) => c.name?.trim() || c.phoneNumber?.trim() || c.email?.trim(),
  )
  const phoneIconUrl = phoneIcon?.url ? getStrapiMedia(phoneIcon.url) : null
  const formStyle = {
    ...(formBg
      ? { backgroundColor: formBg }
      : splitLayout
        ? { backgroundColor: '#FFFFFF' }
        : {}),
    ...(fieldBg
      ? { '--contact-field-bg': fieldBg }
      : splitLayout
        ? { '--contact-field-bg': '#F6F0E3' }
        : {}),
  } as CSSProperties
  const isLinkCta = submitButtonStyle?.displayType === 'Link'
  const ctaTheme = ctaThemeFromLayout(layout)
  const submitClass = [
    isLinkCta
      ? resolveButtonClass(submitButtonStyle, { theme: ctaTheme })
      : resolveContactSubmitButtonClass(ctaTheme),
    'h-[52px] w-full cursor-pointer self-stretch disabled:cursor-not-allowed disabled:opacity-60',
  ].join(' ')

  const statusMessages = (
    <>
      {submitStatus === 'success' && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-card border border-brand-primary bg-surface-white p-4 text-body font-body text-text-dark"
        >
          Thank you for reaching out! Our team will be in touch with you soon.
        </div>
      )}

      {submitStatus === 'error' && serverError && (
        <div
          role="alert"
          aria-live="assertive"
          className="rounded-card border border-[var(--color-error)] p-4 text-body font-body text-[var(--color-error)]"
        >
          {serverError}
        </div>
      )}
    </>
  )

  const formFieldsBlock = (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onFocus={prefetchRecaptcha}
      noValidate
      className={`flex w-full flex-col ${splitLayout ? 'gap-6 md:gap-10' : 'gap-8 md:gap-12'}`}
      aria-label={cardHeading ?? sectionHeading ?? 'Contact form'}
    >
      <div className={`flex w-full flex-col ${splitLayout ? 'gap-4' : 'gap-4'}`}>
        {groupFields(validFields).map((group) => {
          if (group.length === 2) {
            return (
              <div
                key={`${group[0].id}-${group[1].id}`}
                className={`grid grid-cols-1 gap-4 ${
                  splitLayout ? 'md:grid-cols-2 md:gap-6' : 'md:grid-cols-2 md:gap-5'
                }`}
              >
                {group.map((field) => renderField(field))}
              </div>
            )
          }
          return renderField(group[0])
        })}
      </div>

      <div
        className={`flex flex-col items-center gap-4 ${
          splitLayout ? '' : 'md:border-t md:border-border md:pt-6'
        }`}
      >
        <button
          type="submit"
          disabled={submitStatus === 'submitting'}
          aria-busy={submitStatus === 'submitting'}
          className={submitClass}
        >
          {submitStatus === 'submitting'
            ? 'Sending…'
            : (submitButtonLabel ?? 'Send Message')}
          {isLinkCta && submitStatus !== 'submitting' && (
            <span aria-hidden="true" className="text-nav">
              →
            </span>
          )}
        </button>

        {disclaimer && (
          <p
            className={
              splitLayout
                ? 'text-center text-[11px] leading-[14px] font-body font-normal tracking-normal text-text-primary md:text-xs md:leading-4 [&_a]:font-bold [&_a]:underline [&_a]:text-text-primary hover:[&_a]:text-text-dark'
                : 'text-center font-body text-[11px] font-normal leading-[14px] text-stone md:text-xs md:leading-4 [&_a]:underline [&_a]:text-stone hover:[&_a]:text-forest'
            }
            dangerouslySetInnerHTML={{ __html: disclaimer }}
          />
        )}
      </div>
    </form>
  )

  if (splitLayout) {
    return (
      <section
        data-fig="teaser-contact-form"
        data-layout={layout ?? 'Layout A'}
        className={`relative overflow-hidden px-spacing-section-x py-12 md:py-spacing-section-y ${
          sectionBg ? '' : 'bg-[#F4F1EE]'
        }`}
        style={sectionBg ? { backgroundColor: sectionBg } : undefined}
        aria-labelledby={sectionHeading ? 'teaser-contact-heading' : undefined}
        aria-label={sectionHeading ? undefined : 'Contact form'}
      >
        <BackgroundLayers pattern={backgroundPattern} />

        <div className="relative z-10 mx-auto flex w-full max-w-width-content flex-col gap-8 min-[1250px]:flex-row min-[1250px]:items-start min-[1250px]:justify-between min-[1250px]:gap-8">
          <FadeUp
            className="flex w-full flex-col gap-3 min-[1250px]:min-w-0 min-[1250px]:max-w-[calc(538/1200*100%)] min-[1250px]:flex-1 md:gap-6"
            delay={0}
            durationMs={1200}
          >
            {(preHeading || sectionHeading) && (
              <div className="flex flex-col gap-3 md:gap-4">
                {preHeading && (
                  <p
                    data-fig="teaser-contact-preheading"
                    className="font-body text-xs font-bold uppercase leading-[150%] tracking-[1px] text-preheading md:text-sm md:leading-5"
                  >
                    {preHeading}
                  </p>
                )}
                {sectionHeading && (
                  <h2
                    id="teaser-contact-heading"
                    data-fig="teaser-contact-heading"
                    className="font-heading text-[30px] font-bold leading-[120%] text-ink md:text-[48px] md:leading-[56px] md:text-forest"
                  >
                    {sectionHeading}
                  </h2>
                )}
              </div>
            )}

            <div className="flex flex-col gap-5 md:gap-12">
              {description && (
                <p
                  data-fig="teaser-contact-description"
                  className="font-body text-sm font-normal leading-[150%] text-text-secondary md:text-xl md:leading-7"
                >
                  {description}
                </p>
              )}

              {people.length > 0 && (
                <ul className="flex flex-col gap-5 md:gap-6" role="list">
                  {people.map((person) => (
                    <li key={person.id} role="listitem">
                      <ContactPersonRow person={person} phoneIconUrl={phoneIconUrl} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </FadeUp>

          <FadeUp
            data-fig="teaser-contact-form-card"
            className="flex w-full min-w-0 flex-col gap-6 rounded-2xl border border-border bg-surface-white p-5 min-[1250px]:max-w-[calc(630/1200*100%)] min-[1250px]:flex-1 md:gap-6 md:rounded-[24px] md:px-10 md:pb-10 md:pt-12 md:shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]"
            style={formStyle}
            delay={100}
            durationMs={1200}
          >
            {cardHeading && (
              <h3
                data-fig="teaser-contact-form-heading"
                className="font-heading text-[30px] font-bold leading-[120%] text-ink md:text-[32px] md:leading-10 md:text-forest"
              >
                {cardHeading}
              </h3>
            )}
            {statusMessages}
            {formFieldsBlock}
          </FadeUp>
        </div>
      </section>
    )
  }

  return (
    <section
      data-fig="teaser-contact-form"
      data-layout={layout ?? 'Layout B'}
      className={`relative overflow-hidden px-4 py-12 md:px-spacing-section-x md:py-16 ${
        sectionBg ? '' : 'bg-surface-white'
      }`}
      style={sectionBg ? { backgroundColor: sectionBg } : undefined}
      aria-labelledby={sectionHeading ? 'teaser-contact-heading' : undefined}
      aria-label={sectionHeading ? undefined : 'Contact form'}
    >
      <BackgroundLayers pattern={backgroundPattern} />

      <div className="relative z-10 mx-auto flex w-full max-w-width-content flex-col gap-10 md:gap-8">
        <FadeUp
          data-fig="teaser-contact-form-card"
          className={`flex w-full flex-col gap-8 rounded border border-border px-5 py-8 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)] md:gap-12 md:rounded md:px-10 md:pb-10 md:pt-12 ${
            formBg ? '' : 'bg-surface-linen'
          }`}
          style={formStyle}
          delay={100}
          durationMs={1200}
        >
          {(sectionHeading || description) && (
            <div
              className={`flex flex-col gap-3 ${
                headerAlignClass.includes('text-center')
                  ? 'items-start text-left md:items-center md:text-center'
                  : headerAlignClass
              }`}
            >
              {sectionHeading && (
                <FadeUp
                  as="h2"
                  id="teaser-contact-heading"
                  data-fig="teaser-contact-heading"
                  className="w-full font-heading text-[28px] font-bold leading-8 text-[#1C1C1A] md:text-[48px] md:leading-[56px] md:text-forest"
                  delay={0}
                >
                  {sectionHeading}
                </FadeUp>
              )}
              {description && (
                <FadeUp
                  as="p"
                  className="w-full font-body text-sm font-normal leading-[150%] text-text-secondary md:text-base md:leading-6"
                  delay={50}
                >
                  {description}
                </FadeUp>
              )}
            </div>
          )}

          {statusMessages}
          {formFieldsBlock}

          <ContactAgentsSection
            people={people}
            title={agentsHeading}
            className="hidden md:flex"
          />
        </FadeUp>

        <ContactAgentsSection
          people={people}
          title={agentsHeading}
          className="px-2 md:hidden"
        />
      </div>
    </section>
  )
}
